import { useEffect } from 'react';

import { getAmplienceMeta } from '../amplience/getAmplienceMeta';
import type { AmplienceMeta } from '~/types/amplience';

type BodyDataAttributesProps = {
  categoryDetails?: { categoryCode?: string | null } | null;
  plpSlotContent?: { _meta?: AmplienceMeta } | null;
  storyPageContent?: { _meta?: AmplienceMeta } | null;
  genericLayoutSlotContent?: { _meta?: AmplienceMeta } | null;
};

type BodyDataAttributes = {
  'data-category-code'?: string;
  'data-content-id'?: string;
  'data-content-name'?: string;
  'data-content-key'?: string;
};

const BODY_DATA_ATTRIBUTE_NAMES: Array<keyof BodyDataAttributes> = [
  'data-category-code',
  'data-content-id',
  'data-content-name',
  'data-content-key',
];

/**
 * The most specific content wins: PLP slot, then Story Page, then the generic layout slot
 */
export const getBodyDataAttributes = (props?: BodyDataAttributesProps): BodyDataAttributes => {
  const { metaAttributes } = getAmplienceMeta(
    props?.plpSlotContent?._meta ??
      props?.storyPageContent?._meta ??
      props?.genericLayoutSlotContent?._meta
  );

  return {
    'data-category-code': props?.categoryDetails?.categoryCode ?? undefined,
    ...metaAttributes,
  };
};

/**
 * `_document` only renders on the server, so keep the body attributes in sync on client side navigation
 */
export const useBodyDataAttributes = (props?: BodyDataAttributesProps): void => {
  useEffect(() => {
    const attributes = getBodyDataAttributes(props);

    BODY_DATA_ATTRIBUTE_NAMES.forEach(name => {
      const value = attributes[name];

      if (value) {
        document.body.setAttribute(name, value);
      } else {
        document.body.removeAttribute(name);
      }
    });
  }, [props]);
};
